import type { DocSpec, SheetSpec, SlidesSpec } from "@qn/skills/server";

export type ArtifactType =
  | "html"
  | "svg"
  | "markdown"
  | "code"
  | "mermaid"
  | "document"
  | "sheet"
  | "slides";

export interface ArtifactMeta {
  id: string;
  title: string;
  type: ArtifactType;
  version: number;
  shared: boolean;
  shareToken: string | null;
  conversationId: string | null;
  updatedAt: number;
}

export const OFFICE_EXT = { document: "docx", sheet: "xlsx", slides: "pptx" } as const;
export const isOffice = (t: ArtifactType): t is keyof typeof OFFICE_EXT => t in OFFICE_EXT;

const esc = (s: unknown) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const inline = (s: string) =>
  esc(s)
    .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
    .replace(/\*([^*]+)\*/g, "<i>$1</i>");

// Model-written HTML must not be able to phone home: no network of any kind inside previews.
const CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; font-src data:">`;
const BASE_CSS = `body{font:15px/1.65 system-ui,"Segoe UI",Tahoma,sans-serif;margin:0;padding:28px;color:#1f2340;background:#fff}h1,h2,h3{color:#2b2f6b;line-height:1.3}table{border-collapse:collapse;width:100%;margin:12px 0}th,td{border:1px solid #d9d6ee;padding:6px 10px;text-align:start}th{background:#ede9fe}blockquote{border-inline-start:4px solid #7c5cff;margin:12px 0;padding:2px 14px;color:#555}pre{background:#f4f3fb;padding:12px;border-radius:8px;overflow:auto;white-space:pre-wrap}code{font-family:ui-monospace,monospace}@page{size:A4;margin:16mm}`;

export const wrapHtml = (body: string, rtl = false, extraCss = "") =>
  `<!doctype html><html dir="${rtl ? "rtl" : "ltr"}"><head><meta charset="utf-8">${CSP}<meta name="viewport" content="width=device-width,initial-scale=1"><style>${BASE_CSS}${extraCss}</style></head><body>${body}</body></html>`;

export function docToHtml(spec: DocSpec): string {
  const parts: string[] = [];
  if (spec.title) parts.push(`<h1 style="font-size:2em">${inline(spec.title)}</h1>`);
  if (spec.subtitle)
    parts.push(`<p style="color:#6b7280;font-size:1.15em">${inline(spec.subtitle)}</p>`);
  for (const b of spec.blocks) {
    if (b.type === "heading") parts.push(`<h${b.level}>${inline(b.text)}</h${b.level}>`);
    else if (b.type === "paragraph")
      parts.push(
        `<p style="text-align:${b.align === "center" ? "center" : b.align === "justify" ? "justify" : b.align === "end" ? "end" : "start"}">${inline(b.text)}</p>`,
      );
    else if (b.type === "bullets")
      parts.push(
        `<${b.ordered ? "ol" : "ul"}>${b.items.map((i) => `<li>${inline(i)}</li>`).join("")}</${b.ordered ? "ol" : "ul"}>`,
      );
    else if (b.type === "quote") parts.push(`<blockquote>${inline(b.text)}</blockquote>`);
    else if (b.type === "pagebreak") parts.push(`<div style="break-after:page"></div><hr>`);
    else
      parts.push(
        `<table><thead><tr>${b.header.map((h) => `<th>${inline(h)}</th>`).join("")}</tr></thead><tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`,
      );
  }
  return wrapHtml(parts.join("\n"), spec.rtl);
}

export function sheetToHtml(spec: SheetSpec): string {
  const cell = (v: unknown) =>
    v !== null && typeof v === "object"
      ? `<i style="color:#059669">=${esc((v as { f: string }).f)}</i>`
      : esc(v);
  return wrapHtml(
    spec.sheets
      .map(
        (s) =>
          `<h2>${esc(s.name)}</h2><table><thead><tr>${s.columns.map((c) => `<th>${esc(c.header)}</th>`).join("")}</tr></thead><tbody>${s.rows.map((r) => `<tr>${r.map((v) => `<td>${cell(v)}</td>`).join("")}</tr>`).join("")}</tbody></table>`,
      )
      .join(""),
    spec.rtl,
  );
}

export function slidesToHtml(spec: SlidesSpec): string {
  const acc = `#${spec.theme.accent.replace("#", "")}`;
  const dark = spec.theme.dark;
  const list = (a?: string[]) =>
    a?.length ? `<ul>${a.map((x) => `<li>${inline(x)}</li>`).join("")}</ul>` : "";
  const slides = spec.slides.map((s, i) => {
    let body = "";
    if (s.layout === "title")
      body = `<div style="margin-top:14%"><h1 style="font-size:2.4em">${inline(s.title ?? spec.title ?? "")}</h1><p style="opacity:.7;font-size:1.2em">${inline(s.subtitle ?? "")}</p></div>`;
    else if (s.layout === "section")
      body = `<div style="margin-top:20%"><h1 style="font-size:2.2em;color:#fff">${inline(s.title ?? "")}</h1><p style="color:#f3f0ff">${inline(s.subtitle ?? "")}</p></div>`;
    else if (s.layout === "two-column")
      body = `<h2>${inline(s.title ?? "")}</h2><div style="display:grid;grid-template-columns:1fr 1fr;gap:24px">${list(s.left)}${list(s.right)}</div>`;
    else if (s.layout === "table" && s.table)
      body = `<h2>${inline(s.title ?? "")}</h2><table><thead><tr>${s.table.header.map((h) => `<th style="background:${acc};color:#fff">${inline(h)}</th>`).join("")}</tr></thead><tbody>${s.table.rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    else
      body = `<h2>${inline(s.title ?? "")}</h2>${s.text ? `<p>${inline(s.text)}</p>` : list(s.bullets)}`;
    const bg = s.layout === "section" ? acc : dark ? "#0a0e1a" : "#fff";
    const color = s.layout === "section" ? "#fff" : dark ? "#e6e9f5" : "#1f2340";
    return `<section style="aspect-ratio:16/9;background:${bg};color:${color};border-top:6px solid ${acc};border-radius:10px;padding:4% 6%;margin:0 0 18px;position:relative;overflow:hidden;box-shadow:0 2px 14px rgba(0,0,0,.18);break-inside:avoid"><span style="position:absolute;inset-inline-end:14px;bottom:8px;font-size:11px;opacity:.5">${i + 1}</span>${body}${s.notes ? `<p style="position:absolute;inset-inline:6%;bottom:8px;font-size:11px;opacity:.55;margin:0">🗒 ${esc(s.notes).slice(0, 140)}</p>` : ""}</section>`;
  });
  return wrapHtml(slides.join(""), spec.rtl, "body{background:#eceaf6}h2{margin-top:0}");
}

/** Full HTML document for previewing / printing any artifact (except mermaid, which renders async). */
export async function buildPreviewHtml(
  type: ArtifactType,
  content: string,
  mermaidSvg?: string,
): Promise<string> {
  const { marked } = await import("marked");
  try {
    switch (type) {
      case "html":
        return /<head[\s>]/i.test(content)
          ? content.replace(/<head([^>]*)>/i, `<head$1>${CSP}`)
          : `<!doctype html><html><head><meta charset="utf-8">${CSP}</head><body>${content}</body></html>`;
      case "svg":
        return wrapHtml(`<div style="display:grid;place-items:center">${content}</div>`);
      case "markdown":
        return wrapHtml(
          await marked.parse(content, { async: true }),
          /[؀-ۿ]/.test(content.slice(0, 200)),
        );
      case "code":
        return wrapHtml(`<pre dir="ltr">${esc(content)}</pre>`);
      case "mermaid":
        return wrapHtml(
          `<div style="display:grid;place-items:center">${mermaidSvg ?? `<pre>${esc(content)}</pre>`}</div>`,
        );
      case "document":
        return docToHtml(JSON.parse(content));
      case "sheet":
        return sheetToHtml(JSON.parse(content));
      case "slides":
        return slidesToHtml(JSON.parse(content));
    }
  } catch (e) {
    return wrapHtml(
      `<p style="color:#b00020">Preview error: ${esc((e as Error).message)}</p><pre>${esc(content.slice(0, 2000))}</pre>`,
    );
  }
}

export async function renderMermaid(source: string): Promise<string> {
  const mermaid = (await import("mermaid")).default;
  mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme: "default" });
  const { svg } = await mermaid.render(`m${Math.random().toString(36).slice(2)}`, source);
  return svg;
}

/** ASCII-only file names: Chromium drops the whole name (and its extension) for non-ASCII `download` values. */
export const safeName = (title: string, fallback = "artifact") => {
  const ascii = title
    .replace(/[^\x20-\x7E]+/g, " ")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return /[A-Za-z0-9]{2}/.test(ascii)
    ? ascii
    : `${fallback}-${new Date().toISOString().slice(0, 10)}`;
};

function save(bytes: BlobPart, mime: string, filename: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Real Office files are generated in the browser (libraries lazy-loaded). */
export async function downloadArtifact(type: ArtifactType, title: string, content: string) {
  if (isOffice(type)) {
    const { renderArtifact } = await import("@qn/skills");
    const r = await renderArtifact(type, content);
    return save(r.bytes as BlobPart, r.mime, `${safeName(title, type)}.${r.ext}`);
  }
  const ext = { html: "html", svg: "svg", markdown: "md", code: "txt", mermaid: "mmd" }[type];
  const mime = {
    html: "text/html",
    svg: "image/svg+xml",
    markdown: "text/markdown",
    code: "text/plain",
    mermaid: "text/plain",
  }[type];
  save(content, `${mime};charset=utf-8`, `${safeName(title, type)}.${ext}`);
}

/**
 * "Download as PDF" = print the rendered artifact (the browser's shaping handles Arabic/RTL correctly).
 * The frame is sandboxed WITHOUT allow-same-origin, so model-written scripts cannot reach this app; the page prints itself.
 */
export async function printArtifact(type: ArtifactType, content: string) {
  const html = await buildPreviewHtml(
    type,
    content,
    type === "mermaid" ? await renderMermaid(content).catch(() => undefined) : undefined,
  );
  const selfPrint =
    "<style>body{background:#fff!important}</style><script>addEventListener('load',function(){setTimeout(function(){print()},400)})</script>";
  const frame = Object.assign(document.createElement("iframe"), {
    srcdoc: html.replace(/<\/head>/i, `${selfPrint}</head>`),
  });
  frame.style.cssText = "position:fixed;inset-inline-end:0;bottom:0;width:0;height:0;border:0";
  frame.setAttribute("sandbox", "allow-modals allow-scripts");
  document.body.appendChild(frame);
  setTimeout(() => frame.remove(), 120_000);
}
